import React, { useEffect, useState } from "react";
import { Row, Col } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { saveRecords } from "../../services/api.service.js";
import { toast } from "react-toastify";
import { LoaderCreatePage } from "../Loader/Loader.js";
import { loaderOverlay } from "../../Style/loaderOverly.js";
import { createPageData } from "./DispatchedFile.js";
import BasicPopupModel from "../../custom_components/Components/BasicPopupModel.js";
import TextInput from "../../custom/components/Text-Input/TextInput.js";

const DispatchedForm = ({ jobCards = [], onRemoveJobCard }) => {
  const navigate = useNavigate();

  const { payload, headerName, navigatePage, bulkDispatchEndpoint } =
    createPageData();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [popup, setPopup] = useState(false);

  // One remark per job card, keyed by job card id. A common remark can be
  // pushed into every row with "Apply to all".
  const [remarks, setRemarks] = useState({});
  const [commonRemark, setCommonRemark] = useState("");

  // Drop remarks of job cards that are no longer selected.
  useEffect(() => {
    setRemarks((prev) => {
      const next = {};
      jobCards.forEach((jobCard) => {
        next[jobCard.id] = prev[jobCard.id] || "";
      });
      return next;
    });
  }, [jobCards]);

  const getOnlyDate = (date) => {
    if (!date) return "-";

    const d = new Date(date);
    if (isNaN(d.getTime())) return "-";

    return d
      .toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .replace(/\//g, "-");
  };

  const onRemarkChange = (jobCardId, value) => {
    setRemarks((prev) => ({ ...prev, [jobCardId]: value }));
  };

  const applyCommonRemark = () => {
    const next = {};
    jobCards.forEach((jobCard) => {
      next[jobCard.id] = commonRemark;
    });
    setRemarks(next);
  };

  const handleSubmit = async () => {
    try {
      setLoadingSubmit(true);

      const userDetails = JSON.parse(localStorage.getItem("authUser"));
      const userId = userDetails?.id;

      const bulkPayload = {
        schema: payload?.schema,
        dispatched_by: userId,
        dispatches: jobCards.map((jobCard) => ({
          intake_id: jobCard.intake_id,
          remark: remarks[jobCard.id] || null,
        })),
      };

      const responseData = await saveRecords(bulkPayload, bulkDispatchEndpoint);

      if (responseData && responseData.code === 1) {
        toast.success(
          responseData.message ||
            `${jobCards.length} job card(s) dispatched successfully!`,
        );
        navigate(navigatePage?.back);
      } else {
        toast.error(responseData.message || `Error while saving ${headerName}!`);
      }
    } catch (error) {
      console.error("Error during submission:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <div>
      <div className="card p-3">
        <h5 className="mb-3 border border-0">
          Job Card Details{" "}
          <span className="badge bg-primary align-middle">
            {jobCards.length} selected
          </span>
        </h5>

        <div className="container-fluid">
          {jobCards.map((jobCardData) => (
            <div className="card mb-3" key={jobCardData.id}>
              <div className="card-body">
                <Row>
                  {/* First Section (Left Side) */}
                  <Col md="6" className="border-end border-secondary p-3">
                    <p>
                      <strong>Job Card No: </strong>
                      {jobCardData?.job_card_no || "-"}
                    </p>
                    <p>
                      <strong>Engineer Check At: </strong>
                      {jobCardData?.technician_check_at
                        ? getOnlyDate(jobCardData?.technician_check_at)
                        : "-"}
                    </p>

                    <p>
                      <strong>Job Card Status: </strong>
                      {jobCardData?.status || "-"}
                    </p>
                  </Col>

                  {/* Second Section (Right Side) */}
                  <Col md="6" className="p-3">
                    <p>
                      <strong>GMDS Code: </strong>
                      {jobCardData?.intake?.material?.gmds_code ||
                        jobCardData?.intake?.material?.name ||
                        "-"}
                    </p>
                    <p>
                      <strong>Intake No: </strong>
                      {jobCardData?.intake?.intake_no || "-"}
                    </p>
                    <p>
                      <strong>Serial No: </strong>
                      {jobCardData?.intake?.material_serial_no || "-"}
                    </p>
                  </Col>

                  <Col md="6" className="px-3">
                    <TextInput
                      label="Dispatch Remark"
                      name={`remark_${jobCardData.id}`}
                      value={remarks[jobCardData.id] || ""}
                      onChange={(e) =>
                        onRemarkChange(jobCardData.id, e.target.value)
                      }
                      placeholder="Enter Dispatch Remark"
                      inputClassName="form-control"
                      labelClassName="form-label"
                    />
                  </Col>

                  <Col
                    md="6"
                    className="px-3 d-flex align-items-end justify-content-md-end"
                  >
                    {jobCards.length > 1 && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm mb-1"
                        onClick={() => onRemoveJobCard(jobCardData.id)}
                      >
                        Remove
                      </button>
                    )}
                  </Col>
                </Row>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card p-3">
        {jobCards.length > 1 && (
          <Row className="align-items-end">
            <Col md="6">
              <TextInput
                label="Common Dispatch Remark"
                name="common_remark"
                value={commonRemark}
                onChange={(e) => setCommonRemark(e.target.value)}
                placeholder="Enter remark to apply on all job cards"
                inputClassName="form-control"
                labelClassName="form-label"
              />
            </Col>
            <Col md="6" className="mb-3">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={applyCommonRemark}
              >
                Apply to all
              </button>
            </Col>
          </Row>
        )}

        <div className="mt-3 d-flex gap-3 flex-wrap">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setPopup(true)}
          >
            Dispatch
          </button>
          <button
            type="button"
            onClick={() => {
              navigate(navigatePage?.back);
            }}
            className="btn btn-secondary"
          >
            Cancel
          </button>
        </div>
      </div>

      <BasicPopupModel
        isVisible={popup}
        onClose={() => setPopup(false)}
        title={
          jobCards.length > 1
            ? `Are you sure you want to dispatch these ${jobCards.length} job cards?`
            : "Are you sure you want to dispatch this job card?"
        }
      >
        <div className="mt-3">
          <div className="text-center">
            <span>Job Card No: </span>{" "}
            <strong>
              {jobCards.map((jobCard) => jobCard.job_card_no).join(", ")}
            </strong>
          </div>
          <div className="mt-3 d-flex gap-3 flex-wrap justify-content-center">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setPopup(false);
                handleSubmit();
              }}
            >
              Dispatch
            </button>
            <button
              type="button"
              onClick={() => {
                setPopup(false);
              }}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      </BasicPopupModel>
      {loadingSubmit && (
        <div style={loaderOverlay}>
          <LoaderCreatePage />
        </div>
      )}
    </div>
  );
};

export default DispatchedForm;
